import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import ActionDialog from "components/admin/ActionDialog";
import AdminTable from "components/admin/AdminTable";
import GiftEditor, { GiftPreview } from "components/admin/GiftEditor";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { REWARD_KINDS, boardName, parseJson, scoreKeys } from "./shared";

/** Sửa một dòng mốc trong bộ nhớ; chỉ ghi DB khi bấm "Lưu tất cả" ở hộp thoại cha. */
function RewardRowDialog({ row, keys, config, onClose, onSave }) {
  const [form, setForm] = useState(row);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const meta = REWARD_KINDS[form.kind] || REWARD_KINDS[1];
  const keyOptions =
    keys.includes(form.score_key) || !form.score_key ? keys : [...keys, form.score_key];

  return (
    <ActionDialog
      open
      maxWidth="md"
      title={row.id ? `Sửa mốc #${row.id}` : "Thêm mốc"}
      description={meta.hint}
      color="info"
      submitText="Xong"
      disabled={!form.score_key}
      onClose={onClose}
      onSubmit={() =>
        onSave({
          ...form,
          rank_from: Number(form.rank_from) || 0,
          rank_to: Number(form.kind) === 0 ? Number(form.rank_to) || 0 : 0,
        })
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <MDInput
            select
            fullWidth
            label="Loại mốc"
            value={form.kind}
            onChange={(e) => set({ kind: Number(e.target.value) })}
          >
            {Object.entries(REWARD_KINDS).map(([kind, info]) => (
              <MenuItem key={kind} value={Number(kind)}>
                {info.label}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        <Grid item xs={12} md={4}>
          <MDInput
            select
            fullWidth
            label="Bảng điểm"
            value={form.score_key}
            onChange={(e) => set({ score_key: e.target.value })}
          >
            {keyOptions.map((key) => (
              <MenuItem key={key} value={key}>
                {boardName(config, key)} ({key})
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        <Grid item xs={6} md={2}>
          <MDInput
            fullWidth
            type="number"
            label={meta.from}
            value={form.rank_from}
            inputProps={{ min: 0 }}
            onChange={(e) => set({ rank_from: e.target.value })}
          />
        </Grid>
        {Number(form.kind) === 0 && (
          <Grid item xs={6} md={2}>
            <MDInput
              fullWidth
              type="number"
              label="Đến hạng (0 = hết)"
              value={form.rank_to}
              inputProps={{ min: 0 }}
              onChange={(e) => set({ rank_to: e.target.value })}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <MDBox display="flex" alignItems="center">
            <Switch
              checked={Boolean(form.enabled)}
              onChange={(e) => set({ enabled: e.target.checked })}
            />
            <MDTypography variant="caption">Đang áp dụng</MDTypography>
          </MDBox>
        </Grid>
      </Grid>
      <GiftEditor
        value={form.gift}
        onChange={(gift) => set({ gift })}
        label="Quà"
        randomNote="Quà nạp nên là skin, danh hiệu, cánh, gói tài nguyên (thiết kế 97 mục 5.6)."
      />
    </ActionDialog>
  );
}

RewardRowDialog.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.number,
    kind: PropTypes.number,
    score_key: PropTypes.string,
    rank_from: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    rank_to: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    gift: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    enabled: PropTypes.bool,
  }).isRequired,
  keys: PropTypes.arrayOf(PropTypes.string).isRequired,
  config: PropTypes.shape({}).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

function toRow(raw) {
  return {
    id: raw.id,
    kind: Number(raw.kind),
    score_key: raw.score_key,
    rank_from: raw.rank_from,
    rank_to: raw.rank_to,
    gift: parseJson(raw.gift, []),
    enabled: Number(raw.enabled) === 1,
  };
}

function sortRows(rows) {
  return [...rows].sort(
    (a, b) =>
      a.kind - b.kind ||
      String(a.score_key).localeCompare(String(b.score_key)) ||
      Number(a.rank_from) - Number(b.rank_from)
  );
}

/** Mốc quà và thưởng hạng của một mùa (bảng event_reward). */
function RewardsDialog({ season, canEdit, onClose }) {
  const [run, busy] = useRunner();
  const config = useMemo(() => parseJson(season.config, {}), [season.config]);
  const keys = useMemo(() => scoreKeys(config), [config]);
  const loaded = useFetch(
    () => api.get(`/api/admin/event/seasons/${season.id}/rewards`),
    [season.id]
  );
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dirty, setDirty] = useState(false);
  const locked = season.status === 3 || season.status === 9 || !canEdit;

  useEffect(() => {
    if (loaded.data) setRows(sortRows(loaded.data.map(toRow)));
  }, [loaded.data]);

  const upsert = (row) => {
    setRows((list) => {
      const next =
        editing.index >= 0 ? list.map((r, i) => (i === editing.index ? row : r)) : [...list, row];
      return sortRows(next);
    });
    setDirty(true);
    setEditing(null);
  };

  const remove = (index) => {
    setRows((list) => list.filter((_, i) => i !== index));
    setDirty(true);
  };

  const save = async () => {
    const { ok } = await run(
      () => api.put(`/api/admin/event/seasons/${season.id}/rewards`, rows),
      "Đã lưu mốc quà"
    );
    if (ok) {
      setDirty(false);
      loaded.reload();
    }
  };

  const fresh = {
    kind: 1,
    score_key: keys[0] || "",
    rank_from: 1,
    rank_to: 0,
    gift: [],
    enabled: true,
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Mốc quà · {season.name} (#{season.id})
      </DialogTitle>
      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
          {loaded.error && <Alert severity="error">{loaded.error}</Alert>}
          {locked && canEdit && <Alert severity="info">Mùa đã chốt hoặc đã hủy, chỉ xem.</Alert>}
          <Alert severity="info">
            Thưởng hạng và mốc toàn server có <b>deliver = settle</b> gửi thư lúc chốt mùa. Mốc điểm
            người chơi tự nhận ở NPC. Xóa mốc đã có người nhận thì mốc chỉ bị tắt, không mất lịch
            sử.
          </Alert>
          {!locked && (
            <MDBox display="flex" gap={1}>
              <MDButton
                color="info"
                size="small"
                disabled={busy || keys.length === 0}
                onClick={() => setEditing({ index: -1, row: fresh })}
              >
                Thêm mốc
              </MDButton>
              <MDButton color="success" size="small" disabled={busy || !dirty} onClick={save}>
                Lưu tất cả
              </MDButton>
              {dirty && (
                <MDTypography variant="caption" color="warning" alignSelf="center">
                  Có thay đổi chưa lưu
                </MDTypography>
              )}
            </MDBox>
          )}
          <AdminTable
            loading={loaded.loading}
            rows={(rows || []).map((row, index) => ({ ...row, index }))}
            rowKey={(row) => `${row.id || "new"}-${row.index}`}
            emptyText="Chưa có mốc"
            columns={[
              {
                key: "id",
                label: "",
                render: (row) =>
                  row.id ? "" : <Chip size="small" color="success" label="mới thêm" />,
              },
              {
                key: "kind",
                label: "Loại",
                render: (row) => (
                  <Chip size="small" label={REWARD_KINDS[row.kind]?.label || row.kind} />
                ),
              },
              {
                key: "score_key",
                label: "Bảng",
                render: (row) => boardName(config, row.score_key),
              },
              {
                key: "rank_from",
                label: "Cần",
                render: (row) =>
                  row.kind === 0
                    ? `Hạng ${row.rank_from}${row.rank_to ? `–${row.rank_to}` : " trở xuống"}`
                    : Number(row.rank_from).toLocaleString("vi-VN"),
              },
              {
                key: "gift",
                label: "Quà",
                wrap: true,
                render: (row) => <GiftPreview gift={row.gift} />,
              },
              {
                key: "enabled",
                label: "Bật",
                render: (row) => (row.enabled ? "✓" : "—"),
              },
              {
                key: "act",
                label: "",
                render: (row) =>
                  locked ? null : (
                    <MDBox display="flex" gap={0.5}>
                      <MDButton
                        size="small"
                        color="info"
                        variant="text"
                        onClick={() => setEditing({ index: row.index, row })}
                      >
                        Sửa
                      </MDButton>
                      <MDButton
                        size="small"
                        color="error"
                        variant="text"
                        onClick={() => remove(row.index)}
                      >
                        Xóa
                      </MDButton>
                    </MDBox>
                  ),
              },
            ]}
          />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose} disabled={busy}>
          Đóng
        </MDButton>
      </DialogActions>
      {editing && (
        <RewardRowDialog
          row={editing.row}
          keys={keys}
          config={config}
          onClose={() => setEditing(null)}
          onSave={upsert}
        />
      )}
    </Dialog>
  );
}

RewardsDialog.propTypes = {
  season: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    status: PropTypes.number,
    config: PropTypes.string,
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RewardsDialog;
